import React, { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material'
import { createEmergencyOtp } from '../services/descopeEmergencyOtp'

export default function DescopeEmergencyDialog({ open, selection, uid, apiReady, onClose, onSubmit }) {
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [verified, setVerified] = useState(false)
  const [available, setAvailable] = useState(false)
  const [now, setNow] = useState(Date.now())
  const session = useRef(null)
  const lock = useRef(false)
  const version = useRef(0)
  const selectionKey = JSON.stringify(selection)

  useEffect(() => {
    const generation = ++version.current
    setCode(''); setSent(null); setError(''); setBusy(false); setVerified(false)
    lock.current = false
    setAvailable(false)
    if (!open) return undefined
    try {
      session.current = createEmergencyOtp({ ...JSON.parse(selectionKey), uid })
      setAvailable(true)
    }
    catch (err) { setError(err.message) }
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      if (version.current === generation) version.current += 1
      clearInterval(timer)
      session.current?.dispose()
      session.current = null
    }
  }, [open, selectionKey, uid])

  const cooldown = Math.max(0, Math.ceil(((sent?.resendAt || 0) - now) / 1000))
  async function send() {
    if (lock.current || cooldown > 0 || !session.current) return
    lock.current = true; setBusy(true); setError(''); setCode(''); setVerified(false)
    const generation = version.current
    setSent({ resendAt: Date.now() + 60000 })
    try {
      const result = await session.current.send()
      if (generation === version.current) setSent(result)
    } catch (err) { if (generation === version.current) setError(err.message) }
    finally { if (generation === version.current) { lock.current = false; setBusy(false) } }
  }
  async function verify(event) {
    event.preventDefault()
    if (lock.current || !session.current || !/^\d{6}$/.test(code) || verified) return
    lock.current = true; setBusy(true); setError('')
    const generation = version.current
    try {
      const proof = await session.current.verify(code)
      if (generation !== version.current) return
      setCode(''); setVerified(true)
      if (apiReady) await onSubmit(proof)
    } catch (err) { if (generation === version.current) { setError(err.message); setCode('') } }
    finally { if (generation === version.current) { lock.current = false; setBusy(false) } }
  }
  return <Dialog open={open} onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
    <Box component="form" onSubmit={verify}>
      <DialogTitle>E-posta ile çağrı doğrulaması</DialogTitle>
      <DialogContent>
        <Typography fontWeight={700}>{selection.scope === 'nationwide' ? 'Türkiye geneli · 81 il' : `${selection.provinceCodes.length} il için destek çağrısı`}</Typography>
        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>Kayıtlı e-posta adresinize Descope üzerinden tek kullanımlık kod gönderilir. Kodu doğruladığınızda seçtiğiniz kapsam için çağrı başlatılır.</Typography>
        {!apiReady && <Alert severity="info" sx={{ mb: 2 }}>E-posta doğrulamasını deneyebilirsiniz. Çağrı servisi bağlantısı tamamlanana kadar destek çağrısı gönderilmeyecek.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {verified ? <Alert severity="success">E-posta doğrulandı.{!apiReady && ' Çağrı servisi henüz hazır olmadığı için çağrı gönderilmedi.'}</Alert> : <>
          {sent?.maskedEmail && <>
            <Typography variant="body2" sx={{ mb: 2 }}>Kod gönderildi: {sent.maskedEmail}</Typography>
            <TextField autoFocus fullWidth label="6 haneli doğrulama kodu" value={code} disabled={busy} autoComplete="one-time-code"
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6, pattern: '[0-9]{6}' }} />
          </>}
          <Button sx={{ mt: 2, textTransform: 'none' }} onClick={send} disabled={busy || cooldown > 0 || !available}>{cooldown > 0 ? `Yeni kod için ${cooldown} sn bekleyin` : sent ? 'Yeni kod gönder' : 'E-postama kod gönder'}</Button>
        </>}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button disabled={busy} onClick={onClose}>{verified ? 'Kapat' : 'Vazgeç'}</Button>
        {!verified && <Button type="submit" variant="contained" color="error" disabled={busy || !sent?.maskedEmail || code.length !== 6}>{busy ? 'İşleniyor…' : apiReady ? 'Doğrula ve çağrıyı başlat' : 'Kodu doğrula'}</Button>}
      </DialogActions>
    </Box>
  </Dialog>
}
